<script>
import { mapGetters, mapActions } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import candidateProfileImageMixin from '@/mixins/candidateProfileImageMixin'
import CandidateBookings from '@/components/candidate/candidate-bookings.vue'
import CandidateCertifications from '@/components/candidate/candidate-certifications.vue'
import CandidateQualifications from '@/components/candidate/candidate-qualifications.vue'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import CandidateDetailsTab from './candidate-details-tab.vue'
import CandidateDetailsHeader from './candidate-details-header.vue'
import CandidateAvailability from './candidate-availability.vue'

const detailsTabs = {
  details: 'details',
  certifications: 'certifications',
  availability: 'availability',
  bookings: 'bookings',
  qualifications: 'qualifications',
}

export default {
  name: 'CandidateDetailsModal',
  components: {
    CandidateDetailsHeader,
    CandidateDetailsTab,
    CandidateBookings,
    CandidateCertifications,
    CandidateQualifications,
    CandidateAvailability,
    EmptyStateBlock,
  },
  mixins: [baseMixin, candidateProfileImageMixin],
  model: {
    prop: 'show',
    event: 'input',
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    candidate: null,
    error: false,
    tab: 'details',
    currentRequestId: 0,
  }),
  computed: {
    ...mapGetters({
      isLoading: 'candidate/isLoading',
    }),
    profilePicture() {
      return this.$_candidateProfileImageMixin_profilePicture
    },
    tabs() {
      return Object.values(detailsTabs).map((value) => ({
        value,
        label: this.$t(`candidateDetailsPopup.tabs.${value}`),
      }))
    },
    tabItems() {
      const {
        details,
        certifications,
        availability,
        bookings,
        qualifications,
      } = detailsTabs

      return this.tabs
        .map(({ value }) => {
          let component = null
          let props = {
            [value]: this.candidate?.[value],
            timeZone: this.candidate?.timeZone,
          }

          switch (value) {
            case details:
              props = {
                candidate: this.candidate,
                isLoading: this.isLoading,
              }
              component = CandidateDetailsTab
              break
            case certifications:
              component = CandidateCertifications
              break
            case availability:
              props = {
                candidateId: this.id,
                isActive: this.tab === availability,
              }
              component = CandidateAvailability
              break
            case bookings:
              component = CandidateBookings
              break
            case qualifications:
              component = CandidateQualifications
              break
          }

          return {
            value,
            component,
            props,
            hidden: value !== details && !this.candidate,
          }
        })
        .filter(({ hidden }) => !hidden)
    },
    isOpen: {
      get() {
        return this.show
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  async created() {
    await this.fetchCandidate(this.id, ++this.currentRequestId)
  },
  methods: {
    ...mapActions({
      loadCandidate: 'candidate/loadCandidate',
    }),
    async fetchCandidate(id, requestId = this.currentRequestId) {
      const response = await this.loadCandidate(id)

      if (requestId !== this.currentRequestId) return // Discard stale response

      if (!response.isSuccess) {
        this.error = true
        return
      }

      this.candidate = response.data

      await this.$_candidateProfileImageMixin_loadCandidateDisplayPicture(id)
    },
    resetState() {
      this.candidate = null
      this.error = false
      this.tab = 'details'
    },
    close() {
      this.isOpen = false
      this.$emit('closeModal')
    },
  },
}
</script>

<template>
  <BaseModal
    v-model="isOpen"
    header-class="pa-6"
    actions-class="d-flex pa-6"
    hide-cancel
    hide-header-divider
    hide-padding
    width="720"
  >
    <template v-slot:header>
      <CandidateDetailsHeader
        :candidate="candidate"
        :is-loading="isLoading"
        :profile-picture="profilePicture"
        class="flex-grow-1"
      />
    </template>

    <template>
      <v-tabs
        v-model="tab"
        active-class="brand-blue-600-text"
        class="candidate-details-modal__tabs px-6"
      >
        <v-tab
          v-for="{ value, label } in tabs"
          :key="value"
          :href="`#${value}`"
          :disabled="isLoading"
        >
          <span class="text-display-2xs">{{ label }}</span>
        </v-tab>
      </v-tabs>

      <EmptyStateBlock v-if="error" class="candidate-details-modal__tabs">
        <template v-slot:icon>
          <v-icon size="60" color="red lighten-1"
            >mdi-alert-circle-outline</v-icon
          >
        </template>
        <template v-slot:title>
          <h3>{{ $t('candidateDetailsPopup.error.title') }}</h3>
        </template>
        <template v-slot:description>
          <p>{{ $t('candidateDetailsPopup.error.body') }}</p>
        </template>
      </EmptyStateBlock>
      <v-tabs-items
        v-else
        v-model="tab"
        class="candidate-details-modal__tabs overflow-y-auto pa-6"
      >
        <v-tab-item
          v-for="{ value, component, props } in tabItems"
          :key="value"
          :value="value"
        >
          <component :is="component" v-bind="{ ...props }" />
        </v-tab-item>
      </v-tabs-items>
    </template>
    <template v-slot:actions>
      <BaseButton secondary sm width="65" @click="close">
        {{ $t('general.close') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<style lang="scss" scoped>
@import '@/styles/colors';
@import '@/styles/r2b-colours';

::v-deep .v-tab {
  text-transform: none;
  letter-spacing: 0.25px;
}

::v-deep .v-slide-group__content.v-tabs-bar__content {
  border-bottom: 1px solid $color-black-transparent-12;
}

.candidate-details-modal__tabs {
  max-height: 60vh;
}
</style>
